import React, {
  useCallback,
  useEffect, useMemo, useRef,
  useState,
} from 'react';
import MaiaIconImage from '@/media/MaiaIA/iconImage.png';
import * as Styles from './AiHeaderButton.styles';
import { useResize } from '@/hooks/useResize';


const AiHeaderButton = ({
  onClick, headerButtonLabel,
}) => {
  const [currentPhrase, setCurrentPhrase] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [showCursor, setShowCursor] = useState(false);
  const timeoutRef = useRef(null);

  const intervalRef = useRef(null);


  const view = useResize();
  const isMobile = view.width < 960;

  const {
    phraseLastWords, initialPhrase, fixedPhrase, mobilePhrase,
  } = headerButtonLabel;

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const typeText = useCallback((text, startFrom = '', callback = null) => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    let currentText = startFrom;
    const remainingText = text.substring(startFrom.length);
    let index = 0;

    setCurrentPhrase(currentText);
    setShowCursor(true);

    intervalRef.current = setInterval(() => {
      if (index < remainingText.length) {
        currentText += remainingText[index];
        setCurrentPhrase(currentText);
        index += 1;
      } else {
        clearInterval(intervalRef.current);
        callback && callback();
      }
    }, 80);
  }, []);

  const eraseText = useCallback((fromText, toLength, callback = null) => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    let currentText = fromText;
    setShowCursor(true);

    intervalRef.current = setInterval(() => {
      if (currentText.length > toLength) {
        currentText = currentText.slice(0, -1);
        setCurrentPhrase(currentText);
      } else {
        clearInterval(intervalRef.current);
        if (callback) callback();
      }
    }, 50);
  }, []);

  const animateSequence = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);

    const phrases = [
      ...phraseLastWords.map(word => `${initialPhrase}${word}`),
      fixedPhrase,
    ];

    const runAnimation = (index) => {
      if (index >= phrases.length) {
        setShowCursor(false);
        return;
      }

      const currentFullPhrase = phrases[index];

      if (index === 0) {
        typeText(currentFullPhrase, '', () => {
          timeoutRef.current = setTimeout(() => {
            runAnimation(index + 1);
          }, 1500);
        });
      } else {
        const previousPhrase = phrases[index - 1];

        let commonLength = 0;
        const minLength = Math.min(previousPhrase.length, currentFullPhrase.length);

        for (let i = 0; i < minLength; i += 1) {
          if (previousPhrase[i] === currentFullPhrase[i]) {
            commonLength += 1;
          } else {
            break;
          }
        }

        eraseText(previousPhrase, commonLength, () => {
          typeText(currentFullPhrase, currentFullPhrase.substring(0, commonLength), () => {
            timeoutRef.current = setTimeout(() => {
              runAnimation(index + 1);
            }, 1500);
          });
        });
      }
    };

    runAnimation(0);
  }, [isAnimating, typeText, eraseText, initialPhrase, phraseLastWords, fixedPhrase]);

  useEffect(() => {
    if (!isAnimating && currentPhrase === '' && !isMobile) {
      return animateSequence();
    }

    if (isMobile) {
      setCurrentPhrase(mobilePhrase);
    }
  }, [animateSequence, isAnimating, currentPhrase, isMobile, mobilePhrase]);

  const handleOnClick = () => {
    onClick && onClick();
  };

  const buttonImage = useMemo(() => <img src={MaiaIconImage} alt="Maia" />, []);

  return (
    <Styles.Container>
      <Styles.Button onClick={handleOnClick}>
        <Styles.Icon>{buttonImage}</Styles.Icon>
        <Styles.Text>{currentPhrase}</Styles.Text>
        {showCursor && <Styles.BlinkingCursor>|</Styles.BlinkingCursor>}
      </Styles.Button>
    </Styles.Container>
  );
};

export default AiHeaderButton;
